import { showDeleteConfirmation } from '@modules/common';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { switchMap, tap } from 'rxjs/operators';
import { TemplateApI } from '@business/api/template_api';
import { CopyTemplateRequest, FetchTemplateResponse, IndexTemplateStatus, SettingTemplateType, TemplateFolderId, TemplateId } from '@business/entities/template';

export class TemplateBloc extends BaseBloc {
  private readonly templatesRepo = new Repository<FetchTemplateResponse>({
    getItemId: brand => brand.id,
  });

  readonly templates$ = this.templatesRepo.items$;
  readonly selectItem = this.templatesRepo.selectItem;

  onReset() {
    this.templatesRepo.reset();
  }

  fetchTemplates = (full = false, status = 'active', folderId?: TemplateFolderId, type?: SettingTemplateType) => {
    return TemplateApI.fetchTemplates(full, status, folderId, type).pipe(
      this.templatesRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_template_error') }),
    );
  };

  fetchTemplate = (id: TemplateId) => {
    return TemplateApI.fetchTemplate(id).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_template_error') }),
    );
  };

  getItem = (id: TemplateId) => {
    return TemplateApI.fetchTemplate(id).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_template_error') }),
    );
  };

  createTemplate = (data: Partial<FetchTemplateResponse> & { libraryId?: string }) => {
    return TemplateApI.createTemplate(data).pipe(
      this.templatesRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_setting_template'),
      }),
    );
  };

  duplicateTemplate = (id: TemplateId, name: string) => {
    return TemplateApI.duplicateTemplate(id, name).pipe(
      this.templatesRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_setting_template'),
      }),
    );
  };

  updateTemplate = (id: TemplateId, data: Partial<FetchTemplateResponse>) => {
    return TemplateApI.updateTemplate(id, data).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_setting_template'),
      }),
    );
  };

  deleteTemplate = (id: TemplateId, archived = false) => {
    const title = archived ? 'Unarchive Template' : 'Archive Template';
    const message = archived ? 'Do you really want to unarchive this template?' : 'Do you really want to archive this template?';
    return showDeleteConfirmation(title, message).pipe(
      switchMap(() => TemplateApI.deleteTemplate(id)),
      tap(item => {
        if (item) {
          return this.templatesRepo.upsertOne({ item });
        } else {
          return this.templatesRepo.removeOne(id);
        }
      }),
      handleMessage({
        type: archived ? requestMessage('undelete_setting_template') : requestMessage('delete_setting_template'),
      }),
    );
  };

  updateFormData = (id: string, data: string) => {
    return TemplateApI.updateFormData(id, data).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        error: requestMessage('fetch_template_error'),
      }),
    );
  };


  changeStatus = (id: TemplateId, status: IndexTemplateStatus) => {
    return TemplateApI.changeStatus(id, status).pipe(
      this.templatesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('change_template_status'),
      }),
    );
  };

  copyBlockTemplate = (data: CopyTemplateRequest) => {
    return TemplateApI.copyBlockTemplate(data).pipe(
      handleMessage({
        type: requestMessage('copy_template_block'),
      }),
    );
  };
}
